import { debounce } from 'underscore';
import { Icon } from 'js/components/common/Icon';
import { searchFlex } from 'js/utils/search.util';
import Overlay from 'js/components/common/Overlay';
import { InputShallow } from 'js/components/common/Fields/Input';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { mapProps, onlyUpdateForKeys, shouldUpdate, withHandlers, withProps } from 'recompose';
import { compose, equals, intersection, keys, map, omit, pick, pipe, prop, propOr, uniq, unnest } from 'ramda';
import { usePortletConfig, usePortletDispatch, usePortletSelector, usePortletTranslations } from 'js/components/system/Portlet';
import { dispatcher } from 'dispatcher';
import { useFocus } from '@theme/js/hooks/useFocus';
import { setSearchActiveAction } from 'js/components/main/Games/actions';
import { trackGameSearchChange } from 'js/components/main/Games/trackingApi';
import { GameItemBody } from '../../StickySearch/Search/GameItemBody';
import { Link } from 'js/components/common/Link';
import { useGameLink } from '@theme/js/hooks/useGameLink';

import style from './index.scss';

export const GameItem = ({ children, code }) => {
    const { fullGameLink } = useGameLink(code);

    return (
        <li className={style.itemContainer}>
            <Link className={style.item} href={fullGameLink}>
                {children}
            </Link>
        </li>
    );
};

export const highlightStart = `<span class="${style.searchQuery}">`;
export const highlightEnd = '</span>';

export const highlightMatches = ({ value, matchesStart, matchesEnd }) => {
    if (matchesStart.length === 0) {
        return value;
    }

    return value.replace(/(?=.)/g, (char, index) => {
        if (matchesStart.includes(index)) {
            return highlightStart;
        }

        if (matchesEnd.includes(index)) {
            return highlightEnd;
        }

        return '';
    });
};

export const SearchBare = ({
    query,
    result,
    isActive,
    onBlur,
    onFocus,
    handleBlur,
    onChange,
    handleBack,
    handleReset,
    setSearchContainerRef,
    setSearchInputRef,
    setFocus,
    isDesktop,
    handleEscapeBlur,
    className = '',
    activeClassName = '',
    applyActiveSearch
}) => {
    const t = usePortletTranslations();
    const activeCls = isActive ? `${style.active} ${activeClassName}` : '';
    const clsName = `${style.searchContainer} ${className} ${activeCls}`;
    const isSearchIcon = isDesktop || !isActive && !isDesktop;
    const [inputWrapperRef, setFocusedInput] = useFocus();

    const setActiveSearch = () => {
        applyActiveSearch();
        setFocusedInput();
    };

    useEffect(() => {
        document.addEventListener('hotKey:escape', handleEscapeBlur);

        return () => document.removeEventListener('hotKey:escape', handleEscapeBlur);
    }, [handleEscapeBlur]);

    useEffect(() => {
        dispatcher.on('search:applyActive', setActiveSearch);

        return () => {
            dispatcher.off('search:applyActive', setActiveSearch);
        };
    }, [setActiveSearch]);

    return (
        <Fragment>
            {!isDesktop && isActive && <Overlay className={style.overlay} />}
            <div className={clsName} ref={setSearchContainerRef}>
                <div className={style.inputWrapper} ref={inputWrapperRef}>
                    <div className={`${style.inputContainer} ${result.length > 0 && style.isResult}`}>
                        {isSearchIcon && (
                            <Icon type="search"
                                  className={`${style.icon} ${style.search}`}
                                  onClick={setFocus}
                                  aria-label={t('aria-label.search')}
                            />
                        )}
                        {!isDesktop && isActive && (
                            <Icon type="back_arrow"
                                  onClick={handleBack}
                                  className={`${style.icon} ${style.back}`}
                            />
                        )}
                        <InputShallow placeholder={t(`search${!isDesktop ? '.short' : ''}`)}
                            value={query}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            onChange={onChange}
                            ref={setSearchInputRef}
                            className={`${style.input} ${isDesktop && isActive ? 'activeSearch' : ''}`}
                            id={'swiss_search'}
                        />
                        {isActive && query.length > 0 && (
                            <Icon type="close_svg"
                                onClick={handleReset}
                                className={`${style.icon} ${style.close}`}
                            />
                        )}
                        {isActive && result.length > 0 && (
                            <div className={style.counter}>
                                {result.length} {t(`${result.length > 1 ? 'items' : 'item'}`)}
                            </div>
                        )}
                    </div>
                </div>

                <ul className={`${style.list} ${isActive && result.length > 0 ? style.shown : ''}`}>
                    {result.map(({ id, value, source: { image }, matchesStart, matchesEnd }) => (
                        <GameItem key={id} code={id} name={value} handleBlur={handleBlur}>
                            <GameItemBody
                                gameCode={id}
                                image={image}
                                value={value}
                                imageClassName={style.image}
                                matchesStart={matchesStart}
                                matchesEnd={matchesEnd}
                                highlightMatches={highlightMatches}
                            />
                        </GameItem>
                    ))}
                </ul>
            </div>
        </Fragment>
    );
};

export const setSearchGamesFn = ({ games, setSearchGames }) => {
    const searchSource = games.map(game => ({
        id: game.code,
        value: game.name,
        source: game
    }));
    setSearchGames(() => searchFlex(searchSource));
};

export const Search = compose(
    onlyUpdateForKeys(['query', 'result', 'isActive']),
    withProps(() => {
        const isLoggedIn = useUserSelector(prop('isLoggedIn'));
        const games = useAppSelector(prop('games'));
        const [searchGames, setSearchGames] = useState(null);
        const [query, setQuery] = useState('');
        const [result, setResult] = useState([]);
        const [isActive, setIsActive] = useState(false);
        const { reactPortletId } = usePortletConfig();
        const { categories, feedName } = usePortletSelector(pick(['categories', 'feedName']));
        const gameCodes = useMemo(
            () => pipe(
                map(propOr([], 'gameCodes')),
                unnest,
                uniq
            )(categories),
            [categories]
        );
        const dispatch = usePortletDispatch();

        useEffect(() => {
            const availableGames = intersection(keys(games), gameCodes);
            setSearchGamesFn({
                games: availableGames.map((code) => {
                    const { name, images = {} } = games[code];
                    return {
                        code,
                        name,
                        image: !isLoggedIn && images.guest || images.base
                    };
                }),
                setSearchGames
            });
        }, [games, gameCodes, isLoggedIn]);

        return {
            ...useDeviceSelector(pick(['isDesktop', 'os'])),
            reactPortletId,
            searchGames,
            query, setQuery,
            result, setResult,
            isActive, setIsActive,
            feedName,
            dispatch
        };
    }),
    shouldUpdate((props, { query, result, isActive, gameCodes }) =>
        props.query !== query ||
        props.result !== result ||
        props.isActive !== isActive ||
        !equals(props.gameCodes, gameCodes)
    ),
    withHandlers(({
        reactPortletId,
        feedName,
        setIsActive,
        dispatch
    }) => {
        let containerRef;
        let inputRef;
        const setInputFocus = () => inputRef.focus();
        const setInputBlur = () => inputRef && inputRef.blur();

        const setIsActiveWrapper = (isActive) => {
            document.documentElement.classList.toggle(style.hideNavigationBar, isActive);
            setIsActive(isActive);
        };

        const handleBlur = ({ target }) => {
            if (!containerRef.contains(target) || target === containerRef) {
                setIsActiveWrapper(false);
                window.removeEventListener('click', handleBlur);
            }
        };

        const handleExternalAPI = debounce((search) => {
            trackGameSearchChange(reactPortletId, feedName, search);
        }, 1500);

        const setSearchActive = isActive => dispatch(setSearchActiveAction(isActive));
        const applyActiveSearch = () => {
            setIsActive(true);
            setIsActiveWrapper(true);
        };

        return {
            setSearchContainerRef: () => ref => containerRef = ref,
            setSearchInputRef: () => ref => inputRef = ref,
            setFocus: () => () => setInputFocus(),
            handleBlur: () => () => {
                setIsActiveWrapper(false);
                window.removeEventListener('click', handleBlur);
                setSearchActive(false);
            },
            handleEscapeBlur: () => () => {
                setIsActiveWrapper(false);
                window.removeEventListener('click', handleBlur);
                setSearchActive(false);
                setInputBlur();
            },
            applyActiveSearch: () => () => applyActiveSearch(),
            onBlur: () => () => setSearchActive(false),
            onFocus: ({ isDesktop, os }) => () => {
                setIsActiveWrapper(true);
                !isDesktop && window.scrollTo(0, 0);
                setTimeout(() => window.addEventListener('click', handleBlur));

                const isNeedDelay = os === 'ios';

                if (isNeedDelay) {
                    setTimeout(() => scrollTo(0, 0), 300);
                }

                setSearchActive(true);
            },
            onChange: ({ setQuery, setResult, searchGames }) => (event) => {
                const { value } = event.target;
                setQuery(value);
                value.length > 1 ? setResult(searchGames(value.trim())) : setResult([]);
                handleExternalAPI(value.trim());
            },
            handleReset: ({ setQuery, setResult }) => (event) => {
                event.stopPropagation();
                setQuery('');
                setResult([]);
                setInputFocus();
            },
            handleBack: ({ setQuery, setResult }) => (event) => {
                event.stopPropagation();
                setQuery('');
                setResult([]);
                setIsActiveWrapper(false);
                removeEventListener('click', handleBlur);
            }
        };
    }),
    mapProps(omit([
        'dispatch',
        'games',
        'gameCodes',
        'feedName',
        'setQuery',
        'setResult',
        'setIsActive',
        'searchGames',
        'setSearchGames'
    ]))
)(SearchBare);
